
import { computed, defineComponent, ref, shallowRef, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import clamp from 'lodash/clamp'
import SpinnerLoader from '@/components/SpinnerLoader.vue'
import InfoIcon from '../common/InfoIcon.vue'

export default defineComponent({
    name: 'TaskSelector',
    props: {
        focused: Boolean,
        value: { type: null, default: () => [] },
        items: { type: Array, default: () => [] },
        itemValue: { type: String, default: 'id' },
        itemName: { type: String, default: 'name' },
        returnObject: Boolean,
        multiple: Boolean,
        label: { type: String, default: 'Поиск...' },
        disabled: { type: Boolean, default: false },
        openOnClick: { type: Boolean, default: true },
        transition: { type: null, default: 'v-menu-transition' },
        showTooltip: { type: Boolean, default: false },
        menuProps: { type: Object },
        fixedHeight: { type: Object },
        isVars: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        projectId: { type: String, default: null },
        selectedTask: { type: Object, default: null },
        hasYClientsConnection: { type: Boolean, default: false },
    },
    components: { SpinnerLoader, InfoIcon },
    emits: ['input', 'delete-task', 'add-task'],
    setup(props, { emit }) {
        const V_SCROLL_ITEM_HEIGHT = 48
        const V_SCROLL_DEFAULT_HEIGHT = 288

        const searchQuery = ref('')
        const menuOpened = ref(false)
        const forceUpdate = ref(0)
        const isScrolling = ref(false)
        const scrollTimeout = ref<number | null>(null)

        // Добавляем пункт "Добавить задачу" и обрабатываем задачи с иконками удаления
        const enhancedItems = computed(() => {
            const tasks = props.items || []

            // Фильтруем удалённые задачи (они могут быть в планах, но не должны показываться в списке выбора)
            const activeTasks = tasks.filter((task: any) => !task.deletedAt)

            const enhancedTasks = activeTasks.map((task: any) => {
                // Задача может быть удалена, если:
                // 1. У неё есть projectId и он равен projectId текущего проекта
                // 2. Или у неё есть projectId и он не null/undefined/пустой
                const hasProjectId = task.projectId != null && task.projectId !== ''
                const isCurrentProjectTask = hasProjectId && task.projectId === props.projectId
                const isProjectTask = hasProjectId || task.isProjectTask === true

                // Задача НЕ является общей, если она принадлежит проекту
                const canDelete = isCurrentProjectTask || (hasProjectId && !task.isGlobal)

                return {
                    ...task,
                    canDelete,
                }
            })

            // Добавляем пункт "Добавить задачу" в конец списка
            return [
                ...enhancedTasks,
                {
                    id: 'add-new-task',
                    name: 'Добавить задачу',
                    isAddNew: true,
                    canDelete: false,
                },
            ]
        })

        const filteredItems = computed(() => {
            if (searchQuery.value) {
                // Фильтруем только обычные задачи, "Добавить задачу" всегда показываем
                const filteredTasks = enhancedItems.value?.filter((el: any) => {
                    if (el.isAddNew) return true // Всегда показываем "Добавить задачу"
                    return el[props.itemName].match(new RegExp(searchQuery.value, 'i'))
                })
                return filteredTasks
            } else {
                return enhancedItems.value
            }
        })

        const hasFilteredItems = computed(() => {
            return filteredItems.value && filteredItems.value.length > 0
        })

        // const vScrollHeight = computed(() => {
        //   const itemCount = filteredItems.value.length
        //   const calculatedHeight = itemCount * V_SCROLL_ITEM_HEIGHT
        //   // Используем минимальную высоту для корректного отображения
        //   return Math.max(calculatedHeight, Math.min(V_SCROLL_DEFAULT_HEIGHT, itemCount * V_SCROLL_ITEM_HEIGHT))
        // })
        const vScrollHeight = computed(() =>
            clamp(filteredItems.value.length * V_SCROLL_ITEM_HEIGHT, 0, V_SCROLL_DEFAULT_HEIGHT)
        )

        const selected = computed(() => {
            return props.multiple
                ? props.items?.filter((el: any) => props.value.includes(el[props.itemValue]))
                : props.items?.find((el: any) => el[props.itemValue] === props.value)
        })

        const isItemSelected = (item: any) => {
            if (!selected.value) return false
            if (props.multiple) {
                return Array.isArray(selected.value) && selected.value.some((sel: any) => sel && sel[props.itemValue] === item[props.itemValue])
            } else {
                return (selected.value as any) && (selected.value as any)[props.itemValue] === item[props.itemValue]
            }
        }

        const hasAutofill = (item: any) => {
            if (!item || item.isAddNew) return false
            // Если нет подключения к yClients, автозаполнение недоступно
            if (!props.hasYClientsConnection) return false
            const autofillParams = item.autofillParams || item.autofill_params
            if (!autofillParams) return false
            // Проверяем, что autofillParams не пустой объект или массив
            if (typeof autofillParams === 'string') {
                try {
                    const parsed = JSON.parse(autofillParams)
                    return parsed && Object.keys(parsed).length > 0
                } catch {
                    return false
                }
            }
            return autofillParams && typeof autofillParams === 'object' && Object.keys(autofillParams).length > 0
        }

        const select = (item: any) => {
            if (item.disabled) return

            if (item.isAddNew) {
                // Эмитим событие для добавления новой задачи
                emit('add-task')
                closeMenu()
                return
            }

            if (props.multiple) {
                const val = !props.value.includes(item[props.itemValue])
                if (val) {
                    emit('input', props.value.concat(item[props.itemValue]))
                } else {
                    emit(
                        'input',
                        props.value.filter((el: any) => el !== item[props.itemValue])
                    )
                }
            } else {
                emit('input', item[props.itemValue])
                closeMenu()
            }
        }

        const deleteTask = (item: any) => {
            if (item.canDelete) {
                emit('delete-task', item)
            }
        }

        const scrollContainer = ref<HTMLElement | null>(null)
        const menu = ref<any>(null)
        let scrollableElement: HTMLElement | null = null

        const handleScroll = () => {
            // Отключаем тултипы во время скролла
            isScrolling.value = true

            // Очищаем предыдущий таймер, если он есть
            if (scrollTimeout.value !== null) {
                clearTimeout(scrollTimeout.value)
            }

            // Включаем тултипы обратно через 150ms после остановки скролла
            scrollTimeout.value = window.setTimeout(() => {
                isScrolling.value = false
                scrollTimeout.value = null
            }, 150)
        }

        const handleWheel = (event: WheelEvent) => {
            // Проверяем, что событие происходит внутри меню
            if (!menu.value || !menu.value.$el) return
            
            const target = event.target as Node
            if (menu.value.$el.contains(target)) {
                // Проверяем, что это событие происходит в области списка задач
                const menuElement = menu.value.$el
                const cardText = menuElement.querySelector('.v-card__text')
                const virtualScroll = menuElement.querySelector('.v-virtual-scroll')
                
                if (cardText && (cardText.contains(target) || virtualScroll?.contains(target))) {
                    handleScroll()
                }
            }
        }

        const handleTouchMove = (event: TouchEvent) => {
            // Проверяем, что событие происходит внутри меню
            if (!menu.value || !menu.value.$el) return
            
            const target = event.target as Node
            if (menu.value.$el.contains(target)) {
                // Проверяем, что это событие происходит в области списка задач
                const menuElement = menu.value.$el
                const cardText = menuElement.querySelector('.v-card__text')
                const virtualScroll = menuElement.querySelector('.v-virtual-scroll')
                
                if (cardText && (cardText.contains(target) || virtualScroll?.contains(target))) {
                    handleScroll()
                }
            }
        }

        // Отслеживаем открытие меню для добавления обработчика скролла
        watch(
            menuOpened,
            (isOpen) => {
                if (isOpen) {
                    // Добавляем обработчики на весь документ для отслеживания wheel/touchmove внутри меню
                    // Это работает даже если элемент .v-virtual-scroll еще не создан
                    document.addEventListener('wheel', handleWheel, { passive: true, capture: true })
                    document.addEventListener('touchmove', handleTouchMove, { passive: true, capture: true })
                    
                    // Также пытаемся найти элемент и добавить обработчик scroll
                    nextTick(() => {
                        if (menu.value && menu.value.$el) {
                            const menuElement = menu.value.$el
                            
                            const tryAttachScroll = () => {
                                const virtualScrollElement = menuElement.querySelector('.v-virtual-scroll') as HTMLElement
                                if (virtualScrollElement) {
                                    if (scrollableElement !== virtualScrollElement) {
                                        if (scrollableElement) {
                                            scrollableElement.removeEventListener('scroll', handleScroll)
                                        }
                                        scrollableElement = virtualScrollElement
                                        virtualScrollElement.addEventListener('scroll', handleScroll, { passive: true })
                                    }
                                } else {
                                    // Пробуем еще раз через небольшую задержку
                                    setTimeout(tryAttachScroll, 100)
                                }
                            }
                            
                            tryAttachScroll()
                        }
                    })
                } else {
                    // Удаляем обработчики при закрытии меню
                    document.removeEventListener('wheel', handleWheel, true as any)
                    document.removeEventListener('touchmove', handleTouchMove, true as any)
                    
                    if (scrollableElement) {
                        scrollableElement.removeEventListener('scroll', handleScroll)
                        scrollableElement = null
                    }
                }
            },
            { immediate: true }
        )

        onUnmounted(() => {
            // Удаляем обработчик при размонтировании
            document.removeEventListener('wheel', handleWheel, true as any)
            document.removeEventListener('touchmove', handleTouchMove, true as any)
            if (scrollableElement) {
                scrollableElement.removeEventListener('scroll', handleScroll)
                scrollableElement = null
            }
            if (scrollContainer.value) {
                scrollContainer.value.removeEventListener('scroll', handleScroll)
            }
            if (scrollTimeout.value !== null) {
                clearTimeout(scrollTimeout.value)
            }
        })

        const selectedTaskDescription = computed(() => {
            // Если передан selectedTask prop, используем его
            if (props.selectedTask && props.selectedTask.description) {
                return props.selectedTask.description
            }
            
            // Иначе ищем в selected
            if (!selected.value) return ''
            const selectedItem = props.multiple && Array.isArray(selected.value) 
                ? selected.value[0] 
                : selected.value
            return selectedItem ? selectedItem.description || '' : ''
        })

        const openMenu = () => (menuOpened.value = true)
        const closeMenu = () => (menuOpened.value = false)

        const activatorId = computed(() => getActivatorId())
        const getActivatorId = () => 'task-selector-activator' + '-' + uuidv4().slice(0, 10)

        watch(
            menuOpened,
            val => {
                setTimeout(() => (searchQuery.value = ''), 200)
            },
            { immediate: true }
        )

        // Принудительное обновление виртуального скролла при изменении поиска
        watch(searchQuery, async () => {
            await nextTick()
            forceUpdate.value++
        })

        // Принудительное обновление виртуального скролла при изменении списка задач
        watch(
            () => props.items,
            async () => {
                await nextTick()
                forceUpdate.value++
            },
            { deep: true }
        )

        // Принудительное обновление виртуального скролла при изменении enhancedItems
        watch(
            enhancedItems,
            async () => {
                await nextTick()
                forceUpdate.value++
            },
            { deep: true }
        )

        return {
            V_SCROLL_ITEM_HEIGHT,
            vScrollHeight,
            searchQuery,
            menuOpened,
            forceUpdate,
            select,
            selected,
            activatorId,
            filteredItems,
            hasFilteredItems,
            openMenu,
            closeMenu,
            deleteTask,
            selectedTaskDescription,
            isItemSelected,
            hasAutofill,
            isScrolling,
            scrollContainer,
            menu,
        }
    },
})
